import {
  IFeriasAgendadasCompleta,
  IFeriasSolicitadasCompleta,
  ISaldoAVencer,
} from '@/typescript/modulos/calendario-de-trabalho/GerenciamentoDeFerias/dominio/gerenciamentoDeFerias.interface'
import moment from 'moment'
import { IGerenciamentoDeFeriasRepositorio } from '@/typescript/modulos/calendario-de-trabalho/GerenciamentoDeFerias/dominio/gerenciamentoDeFeriasRepositorio.interface'
import { StatusSolicitacao } from '@/typescript/modulos/governanca/solicitacoes/dominio/solicitacoes.enum'
import type { IContextoDeEntradaGestaoDeFerias } from '@/typescript/modulos/calendario-de-trabalho/GerenciamentoDeFerias/apresentacao/state'

/**
 * Mapeamento puro das respostas de gestao-de-ferias (agendadas / solicitadas
 * PENDENTE / saldos-a-vencer) para os itens dos widgets "Férias do time"
 * (HomeGestor) e "Minhas férias" (HomeEmpregado). Linguagem: CONTEXT.md —
 * "marcada" = agendada; "pedida" = solicitação PENDENTE; "data limite" = fim
 * do período concessivo; "dias a marcar" = 30 − saldoDeFeriasUsado.
 */

export const TOTAL_DE_DIAS_DE_FERIAS_POR_PERIODO = 30

export type StatusFeriasWidget = 'marcada' | 'pedida'

export interface IItemProximasFerias {
  idEmpregado: string
  nome: string
  /** Datas em ISO (YYYY-MM-DD...) — formatação de exibição fica no componente. */
  inicio: string
  fim: string
  status: StatusFeriasWidget
}

export interface IItemVencimento {
  idEmpregado: string
  nome: string
  /** Fim do período concessivo (ISO) — data em que o saldo vence. */
  dataLimite: string
  diasAMarcar: number
}

export function montarContextoDeEntradaGestaoDeFerias(
  item: IItemProximasFerias
): IContextoDeEntradaGestaoDeFerias {
  return {
    aba: item.status === 'pedida' ? 'liderados-solicitadas' : 'liderados-agendadas',
    empregado: { id: item.idEmpregado, name: item.nome },
    dataInicial: moment(item.inicio).toDate(),
    dataFinal: moment(item.fim).toDate(),
    buscarAutomaticamente: true
  }
}

export interface IGrupoDeMes {
  /** Chave estável "YYYY-MM" — o componente formata o rótulo localizado. */
  chave: string
  itens: IItemProximasFerias[]
}

const chaveDeData = (data: string): number => new Date(data).getTime() || 0

/** "YYYY-MM-DD" no fuso local — datas ISO sem hora parseiam como UTC e
 * quebrariam a comparação "termina hoje" em UTC−3. */
const chaveDoDia = (data: Date): string => {
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${data.getFullYear()}-${mes}-${dia}`
}

/**
 * Junta agendadas (marcadas) e solicitações PENDENTE (pedidas) numa lista
 * cronológica única. Mantém férias em andamento (fim >= hoje) e descarta o
 * que já terminou — a busca por interseção de intervalos pode trazer eventos
 * que começaram antes da janela.
 */
export function montarProximasFerias(
  agendadas: Pick<IFeriasAgendadasCompleta, 'empregado' | 'intervaloDoEvento'>[],
  solicitadasPendentes: Pick<IFeriasSolicitadasCompleta, 'empregado' | 'intervaloDoEvento'>[],
  hoje: Date
): IItemProximasFerias[] {
  const diaDeHoje = chaveDoDia(hoje)

  const paraItem = (
    ferias: Pick<IFeriasAgendadasCompleta, 'empregado' | 'intervaloDoEvento'>,
    status: StatusFeriasWidget
  ): IItemProximasFerias => ({
    idEmpregado: ferias.empregado?.id || '',
    nome: ferias.empregado?.nome || '',
    inicio: ferias.intervaloDoEvento?.inicio || '',
    fim: ferias.intervaloDoEvento?.fim || '',
    status,
  })

  return [
    ...agendadas.map((ferias) => paraItem(ferias, 'marcada')),
    ...solicitadasPendentes.map((ferias) => paraItem(ferias, 'pedida')),
  ]
    .filter((item) => item.inicio && String(item.fim).slice(0, 10) >= diaDeHoje)
    .sort((a, b) => chaveDeData(a.inicio) - chaveDeData(b.inicio))
}

/**
 * Saldos a vencer → lista "precisam marcar", ordenada da data limite mais
 * próxima para a mais distante.
 */
export function montarVencimentos(saldos: ISaldoAVencer[]): IItemVencimento[] {
  return saldos
    .map((saldo) => ({
      idEmpregado: saldo.empregado?.id || '',
      nome: saldo.empregado?.nome || '',
      dataLimite: saldo.periodoConcessivo?.fim || '',
      diasAMarcar: Math.max(
        0,
        TOTAL_DE_DIAS_DE_FERIAS_POR_PERIODO - (saldo.saldoDeFeriasUsado || 0)
      ),
    }))
    .filter((item) => item.dataLimite && item.diasAMarcar > 0)
    .sort((a, b) => chaveDeData(a.dataLimite) - chaveDeData(b.dataLimite))
}

export interface IEntidadesDeConsulta {
  tipo: 'grupo' | 'empregado' | 'local_de_trabalho'
  ids: string[]
}

interface IEmpregadoComLiderDireto {
  id: string
  usuarioLiderDireto?: { id?: string } | null
}

/**
 * O endpoint legado de empregados devolve todo o escopo visível ao usuário.
 * Ao agrupar a Home por líder, recortamos esse escopo pela liderança direta
 * informada em cada empregado antes de consultar as férias.
 */
export function filtrarEmpregadosPorLiderDireto<T extends IEmpregadoComLiderDireto>(
  empregados: T[],
  idLiderDireto: string
): T[] {
  return empregados.filter(
    (empregado) => empregado.usuarioLiderDireto?.id === idLiderDireto
  )
}

/**
 * Traduz os parâmetros de agrupamento da Tela Inicial (montarParametrosDeAgrupamento)
 * para as `entidades` aceitas pelos endpoints de gestao-de-ferias.
 */
export function montarEntidadesDoAgrupamento(parametros: {
  tipo?: string
  userGroups?: string[]
  employees?: string[]
  company?: string
}): IEntidadesDeConsulta | null {
  if (parametros.tipo === 'grupo' && parametros.userGroups?.length) {
    return { tipo: 'grupo', ids: parametros.userGroups }
  }
  if (parametros.tipo === 'liderDireto' && parametros.employees?.length) {
    return { tipo: 'empregado', ids: parametros.employees }
  }
  if (parametros.company) {
    return { tipo: 'local_de_trabalho', ids: [parametros.company] }
  }
  return null
}

export const DIAS_DE_JANELA_DO_WIDGET = 180
const POR_PAGINA = 30
// Teto defensivo de paginação (150 itens por lista) — o rodapé "+N" usa o
// totalDeItens da API, então nada é silenciosamente omitido.
const MAXIMO_DE_PAGINAS = 5

export interface IDadosDeFerias {
  proximas: IItemProximasFerias[]
  vencimentos: IItemVencimento[]
  totalProximas: number
  totalVencimentos: number
}

interface IPaginaDeResposta<T> {
  resultado: T[]
  totalDeItens: number
  totalDePaginas: number
}

async function buscarTodasAsPaginas<T>(
  buscarPagina: (pagina: number) => Promise<IPaginaDeResposta<T>>
): Promise<{ itens: T[]; totalDeItens: number }> {
  const primeira = await buscarPagina(0)
  const itens = [...(primeira.resultado || [])]
  const totalDePaginas = Math.min(primeira.totalDePaginas || 0, MAXIMO_DE_PAGINAS)

  const restantes = await Promise.all(
    Array.from({ length: Math.max(0, totalDePaginas - 1) }, (_, indice) => buscarPagina(indice + 1))
  )
  restantes.forEach((pagina) => itens.push(...(pagina.resultado || [])))

  return { itens, totalDeItens: primeira.totalDeItens || itens.length }
}

type RepositorioDeConsultaDeFerias = Pick<
  IGerenciamentoDeFeriasRepositorio,
  'obterFeriasAgendadas' | 'obterFeriasSolicitadas' | 'obterSaldosAVencer'
>

/**
 * Composição completa dos dados do widget: agendadas + solicitadas PENDENTE
 * (janela hoje → +180d) + saldos a vencer em 180 dias, para as `entidades`
 * do escopo (time do gestor ou o próprio empregado).
 */
export async function buscarDadosDeFerias(
  repositorio: RepositorioDeConsultaDeFerias,
  entidades: IEntidadesDeConsulta,
  hoje: Date
): Promise<IDadosDeFerias> {
  const dataFinal = new Date(hoje)
  dataFinal.setDate(dataFinal.getDate() + DIAS_DE_JANELA_DO_WIDGET)

  const [agendadas, solicitadas, saldos] = await Promise.all([
    buscarTodasAsPaginas((pagina) =>
      repositorio
        .obterFeriasAgendadas({
          entidades,
          dataInicial: hoje,
          dataFinal,
          paginacao: { pagina, por_pagina: POR_PAGINA },
        })
        .then((resposta) => resposta.data)
    ),
    buscarTodasAsPaginas((pagina) =>
      repositorio
        .obterFeriasSolicitadas({
          entidades,
          dataInicial: hoje,
          dataFinal,
          status: StatusSolicitacao.Pendente,
          paginacao: { pagina, por_pagina: POR_PAGINA },
        })
        .then((resposta) => resposta.data)
    ),
    buscarTodasAsPaginas((pagina) =>
      repositorio
        .obterSaldosAVencer({
          entidades,
          quantidadeDeDiasAteOVencimento: DIAS_DE_JANELA_DO_WIDGET,
          paginacao: { pagina, por_pagina: POR_PAGINA },
        })
        .then((resposta) => resposta.data)
    ),
  ])

  const proximas = montarProximasFerias(agendadas.itens, solicitadas.itens, hoje)
  const vencimentos = montarVencimentos(saldos.itens)

  return {
    proximas,
    vencimentos,
    totalProximas: agendadas.totalDeItens + solicitadas.totalDeItens,
    totalVencimentos: saldos.totalDeItens,
  }
}

let repositorioSingleton: RepositorioDeConsultaDeFerias | null = null

/**
 * Repositório de férias construído sob demanda pelos módulos Vuex das Homes:
 * importar '@/main' no topo criaria ciclo (main → store → state das Homes),
 * então o singleton resolve os imports na primeira chamada.
 */
export async function obterRepositorioDeFerias(): Promise<RepositorioDeConsultaDeFerias> {
  if (!repositorioSingleton) {
    const [{ ServicoHttpCondominio }, { GerenciamentoDeFeriasRepositorio }] = await Promise.all([
      import('@/main'),
      import(
        '@/typescript/modulos/calendario-de-trabalho/GerenciamentoDeFerias/repositorio/gerenciamentoDeFeriasRepositorio'
      ),
    ])
    repositorioSingleton = new GerenciamentoDeFeriasRepositorio(ServicoHttpCondominio)
  }
  return repositorioSingleton
}

/** Agrupa a lista cronológica por mês de início, preservando a ordem. */
export function agruparPorMesDeInicio(itens: IItemProximasFerias[]): IGrupoDeMes[] {
  const grupos: IGrupoDeMes[] = []
  itens.forEach((item) => {
    const chave = String(item.inicio).slice(0, 7)
    const grupo = grupos[grupos.length - 1]
    if (grupo && grupo.chave === chave) {
      grupo.itens.push(item)
      return
    }
    grupos.push({ chave, itens: [item] })
  })
  return grupos
}
